/**
 * V2 unread-follows badge — "3 new yoinks from explorers you follow".
 *
 * The header bell (src/ui/alerts.js) counts yoinks logged by the USERS
 * you follow (user_follows — the additive extra; category follows stay
 * out of the count). Read state is two localStorage layers per user on
 * this device (a per-device badge like a phone's message counter, no
 * schema change): a "last seen" stamp that everything older than is read,
 * plus per-row read marks (id → created_at) for yoinks opened one at a
 * time from the panel — opening the panel alone marks nothing; each row
 * tap counts the badge down by one, and "Clear all" (or reading the last
 * row) collapses the marks back into the stamp.
 *
 * Data comes from the get_followed_users_yoinks RPC
 * (supabase/migrations/20260716_unread_follows.sql): the followee fan-out joins server-side
 * (scale rule 3). Until that SQL is applied the call 404s and the bell
 * stays hidden for the session (unreadUnavailable — the lists pattern).
 *
 * Store slices owned here: unreadFollows (null until the first check
 * lands, then the unread rows, [] = caught up), unreadUnavailable.
 */
import * as gateway from '../../core/gateway.js';
import { cached, invalidate } from '../../core/query.js';
import { FEED_SELECT } from '../../ui/card.js';

const seenKey = (userId) => `yoink-v2-follows-seen:${userId}`;
const seenIdsKey = (userId) => `yoink-v2-follows-seen-ids:${userId}`;

/** Badge cap — the fetch asks for one more row so the UI can say "50+" honestly. */
export const UNREAD_CAP = 50;

/** How often a signed-in, visible session re-checks (plus visibilitychange). */
const REFRESH_MS = 5 * 60_000;

/**
 * The last-seen timestamp for this user on this device. First run seeds
 * it with "now": everything already out there starts as read — the badge
 * counts what arrives from here on, not a scary backlog.
 */
export function getLastSeen(userId) {
  const now = new Date().toISOString();
  try {
    const stored = localStorage.getItem(seenKey(userId));
    if (stored) return stored;
    localStorage.setItem(seenKey(userId), now);
  } catch {
    // Private mode / storage full — the badge is session-blind, nothing breaks
  }
  return now;
}

/**
 * Per-row read marks: id → created_at for yoinks opened individually from
 * the panel. The created_at rides along so markSeen can advance the stamp
 * past a read row even when the server's clock ran ahead of the device's.
 */
function getSeenMap(userId) {
  try {
    const parsed = JSON.parse(localStorage.getItem(seenIdsKey(userId)));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch {
    // Missing / corrupt / private mode — nothing individually read
  }
  return {};
}

function saveSeenMap(userId, map) {
  try {
    if (Object.keys(map).length) localStorage.setItem(seenIdsKey(userId), JSON.stringify(map));
    else localStorage.removeItem(seenIdsKey(userId));
  } catch {
    // see getLastSeen
  }
}

/** Write the slice only when it actually changed — store noise is never free. */
function setRows(store, forUserId, rows) {
  const state = store.get();
  if (state.user?.id !== forUserId) return; // signed out / switched mid-flight
  const prev = state.unreadFollows;
  if (prev && prev.length === rows.length && prev[0]?.id === rows[0]?.id) return;
  store.set({ unreadFollows: rows });
}

/** Re-check for unread yoinks. `fresh` bypasses the query cache (app-return paths). */
export async function refreshUnread(store, { fresh = false } = {}) {
  const { user, followingIds, unreadUnavailable } = store.get();
  if (!user || unreadUnavailable) return;
  if (!(followingIds || []).length) {
    setRows(store, user.id, []);
    return;
  }
  const since = getLastSeen(user.id);
  if (fresh) invalidate('unread-follows:');
  let rows;
  try {
    rows = await cached(`unread-follows:${user.id}:${since}`, async () => {
      const { data, error } = await gateway.rawRpc('get_followed_users_yoinks', {
        params: { p_since: since, p_limit: UNREAD_CAP + 1 },
        select: FEED_SELECT,
      });
      if (error) throw error;
      return data || [];
    });
  } catch (err) {
    // 404 = supabase/migrations/20260716_unread_follows.sql hasn't been applied — hide the
    // bell for the session; anything else is a blip, keep what we showed
    if (/HTTP 404/.test(err?.message || '')) store.set({ unreadUnavailable: true });
    return;
  }
  if (getLastSeen(user.id) !== since) return; // marked seen while in flight
  // Individually-read rows stay read across refreshes; marks whose row no
  // longer comes back (deleted, or aged past the stamp) are pruned so the
  // map never outgrows one fetch page.
  const seen = getSeenMap(user.id);
  const seenIds = Object.keys(seen);
  if (seenIds.length) {
    const fetched = new Set(rows.map((row) => String(row.id)));
    const kept = {};
    for (const id of seenIds) if (fetched.has(id)) kept[id] = seen[id];
    if (Object.keys(kept).length !== seenIds.length) saveSeenMap(user.id, kept);
    rows = rows.filter((row) => !(String(row.id) in kept));
  }
  setRows(store, user.id, rows);
}

/**
 * One yoink was opened from the panel: mark just that row read so the
 * badge counts down instead of resetting. Reading the last unread row
 * collapses everything back into the last-seen stamp, so the read marks
 * never outlive the rows they filter.
 */
export function markSeenOne(store, yoinkId) {
  const { user, unreadFollows } = store.get();
  if (!user) return;
  const rows = unreadFollows || [];
  const target = rows.find((row) => String(row.id) === String(yoinkId));
  if (!target) return;
  if (rows.length === 1) {
    markSeen(store);
    return;
  }
  const seen = getSeenMap(user.id);
  seen[String(target.id)] = target.created_at;
  saveSeenMap(user.id, seen);
  store.set({ unreadFollows: rows.filter((row) => row !== target) });
}

/**
 * Everything unread is now seen ("Clear all", or the last row was read):
 * advance last-seen, absorb the per-row read marks, and clear the badge.
 * The stamp is the newest of "now", the newest unread row, and the newest
 * individually-read row — the server's clock can run ahead of the
 * device's, and a row created_at "in the future" would otherwise stay
 * unread forever.
 */
export function markSeen(store) {
  const { user, unreadFollows } = store.get();
  if (!user) return;
  let seenMs = Date.now();
  const seen = getSeenMap(user.id);
  const stamps = (unreadFollows || []).map((row) => row.created_at).concat(Object.values(seen));
  for (const createdAt of stamps) {
    const t = Date.parse(createdAt);
    if (!Number.isNaN(t) && t > seenMs) seenMs = t;
  }
  try {
    localStorage.setItem(seenKey(user.id), new Date(seenMs).toISOString());
  } catch {
    // see getLastSeen
  }
  saveSeenMap(user.id, {});
  invalidate('unread-follows:');
  if ((store.get().unreadFollows || []).length) store.set({ unreadFollows: [] });
}

/**
 * Wire the badge to the session: check when the follow list (re)loads,
 * when the tab/app comes back to the foreground — the "I was offline,
 * now I'm back" moment — and on a slow tick while visible.
 */
export function initUnread(store) {
  let lastFollowingIds = null;
  const unsubscribe = store.subscribe((state) => {
    if (state.followingIds === lastFollowingIds) return;
    lastFollowingIds = state.followingIds;
    if (state.user) refreshUnread(store);
    else if ((state.unreadFollows || []).length) store.set({ unreadFollows: [] });
  });

  const onVisibility = () => {
    if (document.visibilityState === 'visible') refreshUnread(store, { fresh: true });
  };
  document.addEventListener('visibilitychange', onVisibility);
  const timer = setInterval(() => {
    if (document.visibilityState === 'visible') refreshUnread(store, { fresh: true });
  }, REFRESH_MS);

  refreshUnread(store);

  return () => {
    unsubscribe();
    document.removeEventListener('visibilitychange', onVisibility);
    clearInterval(timer);
  };
}
